import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, ClipboardList, Send, XCircle, Bookmark } from "lucide-react";
import { PortalPage, Btn, Card, Spinner, ErrorNote } from "@/components/PortalPage";
import { QuizQuestions, AttemptNotes } from "@/components/LsoParts";
import { LessonSection, ModuleProgress } from "@/components/LsoLesson";
import { lsoTrack, qs } from "@/lib/lso";
import { useAuth } from "@/context/AuthContext";

const draftKey = (uid, kind, as, n) => `alr_lso_draft_${uid}_${kind}_${as}_${n}`;

export default function LsoModule({ kind = "staff" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { num } = useParams();
  const [params] = useSearchParams();
  const n = parseInt(num, 10) || 1;
  const as = kind === "rental" ? "me" : params.get("as") || "me";
  const track = lsoTrack(kind, as);
  const hub = `${track.hubPath}${qs(as)}`;
  const [course, setCourse] = useState(null);
  const [mode, setMode] = useState("lesson");
  const [answers, setAnswers] = useState([]);
  const [restored, setRestored] = useState(false);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const key = draftKey(user.id, kind, as, n);

  useEffect(() => {
    setMode("lesson"); setResult(null); setErr(null);
    track.getCourse().then((c) => {
      setCourse(c);
      const m = c.modules.find((x) => x.number === n);
      const len = m?.quiz?.length || 0;
      const draft = JSON.parse(localStorage.getItem(key) || "null");
      if (draft?.answers?.length === len) {
        setAnswers(draft.answers);
        setRestored(draft.answers.some((a) => a >= 0));
        if (draft.lesson_done && len) setMode("quiz");
      } else setAnswers(new Array(len).fill(-1));
    }).catch((e) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, as, kind]);

  const mod = course?.modules.find((x) => x.number === n);
  const total = course?.modules.length || 0;
  const pass = course?.pass_threshold_percent || 80;
  const saveDraft = (patch) => localStorage.setItem(key, JSON.stringify({ ...(JSON.parse(localStorage.getItem(key) || "{}")), ...patch }));

  const startQuiz = async () => {
    setBusy(true); setErr(null);
    try {
      await track.completeLesson(n);
      if (mod.quiz?.length) { saveDraft({ lesson_done: true, answers }); setMode("quiz"); window.scrollTo(0, 0); }
      else { await track.submitQuiz(n, []); localStorage.removeItem(key); navigate(hub); }
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const submit = async () => {
    if (answers.some((a) => a < 0)) return setErr("Please select an answer for each question before submitting.");
    setBusy(true); setErr(null);
    try {
      const r = await track.submitQuiz(n, answers);
      setResult(r); setMode("result"); window.scrollTo(0, 0);
      if (r.passed) localStorage.removeItem(key);
    } catch (e) {
      setErr(e.detail?.cooldown_until ? `Max attempts reached. Try again after ${new Date(e.detail.cooldown_until).toLocaleString()}.` : e.message);
    } finally { setBusy(false); }
  };

  const retry = () => {
    const cleared = new Array(mod.quiz.length).fill(-1);
    setAnswers(cleared); setResult(null); setRestored(false); setMode("quiz");
    saveDraft({ answers: cleared, lesson_done: true });
  };

  if (!mod) return <PortalPage title="Module" back={hub} testId="lso-module-page">{err ? <ErrorNote>{err}</ErrorNote> : <Spinner />}</PortalPage>;

  return (
    <PortalPage eyebrow={`MODULE ${mod.number} OF ${total}`} title={mod.title} back={hub} backLabel="Course hub" testId="lso-module-page">
      {mode === "lesson" && (
        <div data-testid="lso-lesson">
          <ModuleProgress n={mod.number} total={total} />
          {mod.summary && <p className="mb-8 border-l-2 border-[var(--accent)] pl-4 text-base italic text-neutral-300" data-testid="lso-module-summary">{mod.summary}</p>}
          {mod.sections.map((s, i) => <LessonSection key={i} s={s} i={i} />)}
          <ErrorNote>{err}</ErrorNote>
          <Btn busy={busy} onClick={startQuiz} data-testid="lso-start-quiz"><ClipboardList size={16} /> {mod.quiz?.length ? "Take Module Quiz" : "Mark Lesson Complete"}</Btn>
        </div>
      )}
      {mode === "quiz" && (
        <div data-testid="lso-quiz">
          {restored && <p className="mb-3 flex items-center gap-2 rounded-lg border border-[var(--accent)] bg-[var(--accent)]/10 px-3 py-2 text-xs font-semibold" data-testid="lso-resume-banner"><Bookmark size={14} /> Progress restored — we saved your previous answers.</p>}
          <p className="mb-4 text-sm italic text-neutral-400">Select the best answer for each question. {pass}% required to pass.</p>
          <QuizQuestions questions={mod.quiz} answers={answers} onChange={(a) => { setAnswers(a); setRestored(false); saveDraft({ answers: a }); }} />
          <ErrorNote>{err}</ErrorNote>
          <Btn className="mt-4" busy={busy} onClick={submit} data-testid="lso-submit-quiz"><Send size={14} /> Submit Answers</Btn>
        </div>
      )}
      {mode === "result" && result && (
        <Card className="flex flex-col items-center p-8 text-center" data-testid="lso-quiz-result">
          {result.passed ? <CheckCircle2 size={64} className="text-[var(--accent)]" /> : <XCircle size={64} className="text-[#FF3333]" />}
          <p className="mt-3 font-display text-3xl font-bold uppercase">{result.passed ? "Module Passed!" : "Not Quite There"}</p>
          <p className={`my-2 font-display text-6xl font-extrabold ${result.passed ? "text-[var(--accent)]" : "text-[#FF3333]"}`} data-testid="lso-quiz-score">{Math.round(result.score)}%</p>
          <p className="mb-3 text-sm text-neutral-400">{result.correct} of {result.total} correct • {pass}% to pass</p>
          <AttemptNotes r={result} />
          <div className="mt-4 w-full space-y-2">
            {result.passed ? (
              <Btn onClick={() => navigate(n < total ? `${track.hubPath}/module/${n + 1}${qs(as)}` : hub)} data-testid="lso-continue">{n < total ? `Continue to Module ${n + 1}` : "Back to Hub"}</Btn>
            ) : result.in_cooldown ? (
              <Btn variant="outline" onClick={() => navigate(hub)} data-testid="lso-back-hub">Back to Hub</Btn>
            ) : (
              <>
                <Btn onClick={retry} data-testid="lso-retry">Retry Quiz</Btn>
                <Btn variant="outline" onClick={() => { setMode("lesson"); setResult(null); }} data-testid="lso-review-lesson">Review Lesson</Btn>
              </>
            )}
          </div>
        </Card>
      )}
    </PortalPage>
  );
}
